/**
 * AirlineSelectionModal - Modal for selecting airline(s) after login
 *
 * Shown when:
 * - User logs in and has multiple airlines
 * - User needs to explicitly choose their working context
 *
 * Features:
 * - Multi-select or single-select mode
 * - Shows role, document count, last access
 * - Remember choice option
 * - Sticky footer with always-visible Continue button
 * - Cannot be dismissed (must select)
 */

import React, { useState, useEffect } from 'react';
import FocusLock from 'react-focus-lock';
import { X, Globe, Building, Clock } from 'lucide-react';
import { UserAllowedAirline } from '../../contexts/AirlineContext';
import { supabase } from '../../lib/supabase';
import { GLOBAL_AIRLINE_ID } from '../../lib/constants';

// =====================================================================
// Types
// =====================================================================

interface AirlineSelectionModalProps {
  airlines: UserAllowedAirline[];
  defaultAirlineId?: string;
  onSelect: (airline: UserAllowedAirline | UserAllowedAirline[], remember: boolean) => void;
  userName?: string;
  canDismiss?: boolean;
  multiSelect?: boolean; // NEW: Enable multi-airline selection
}

interface AirlineStats {
  airline_id: string;
  doc_count: number;
  last_access?: string;
}

// =====================================================================
// Component
// =====================================================================

export const AirlineSelectionModal: React.FC<AirlineSelectionModalProps> = ({
  airlines: airlinesProp,
  defaultAirlineId,
  onSelect,
  userName,
  canDismiss = false,
  multiSelect = false,
}) => {
  const airlines = airlinesProp.filter((a) => a.airline_id !== GLOBAL_AIRLINE_ID);

  // Debug: Log airlines data
  console.log('[AirlineSelectionModal] Airlines:', JSON.stringify(airlines, null, 2));
  console.log('[AirlineSelectionModal] Default airline ID:', defaultAirlineId);
  console.log('[AirlineSelectionModal] First airline:', airlines[0]);

  // Single-select state
  const [selectedId, setSelectedId] = useState<string>(
    defaultAirlineId || airlines[0]?.airline_id || ''
  );

  // Multi-select state - Initialize with default airline if provided
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    const initialSet = new Set<string>();
    if (defaultAirlineId) {
      initialSet.add(defaultAirlineId);
    } else if (airlines[0]?.airline_id) {
      initialSet.add(airlines[0].airline_id);
    }
    return initialSet;
  });

  const [remember, setRemember] = useState(false);
  const [stats, setStats] = useState<AirlineStats[]>([]);

  // Fetch real doc counts and last access per airline
  useEffect(() => {
    const fetchStats = async () => {
      const airlineIds = airlines.map((a) => a.airline_id);
      if (airlineIds.length === 0) return;

      // Fetch doc counts per airline
      const { data: docCounts } = await supabase
        .from('doc_library')
        .select('org_id')
        .eq('is_active', true)
        .in('org_id', airlineIds);

      // Fetch last access per airline from audit_logs
      const { data: lastAccess } = await supabase
        .from('audit_logs')
        .select('airline_id, created_at')
        .in('airline_id', airlineIds)
        .order('created_at', { ascending: false });

      // Build stats
      const countMap: Record<string, number> = {};
      docCounts?.forEach((row) => {
        countMap[row.org_id] = (countMap[row.org_id] || 0) + 1;
      });

      const accessMap: Record<string, string> = {};
      lastAccess?.forEach((row) => {
        if (!accessMap[row.airline_id]) {
          const date = new Date(row.created_at);
          const now = new Date();
          const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays === 0) accessMap[row.airline_id] = 'Today';
          else if (diffDays === 1) accessMap[row.airline_id] = 'Yesterday';
          else accessMap[row.airline_id] = `${diffDays} days ago`;
        }
      });

      setStats(
        airlineIds.map((id) => ({
          airline_id: id,
          doc_count: countMap[id] || 0,
          last_access: accessMap[id],
        }))
      );
    };

    fetchStats();
  }, [airlines]);

  // ===================================================================
  // Handlers
  // ===================================================================

  const handleToggleAirline = (airlineId: string) => {
    if (multiSelect) {
      const newSelected = new Set(selectedIds);
      if (newSelected.has(airlineId)) {
        newSelected.delete(airlineId);
      } else {
        newSelected.add(airlineId);
      }
      setSelectedIds(newSelected);
    } else {
      setSelectedId(airlineId);
    }
  };

  const handleSubmit = () => {
    if (multiSelect) {
      const selected = airlines.filter((a) => selectedIds.has(a.airline_id));
      if (selected.length > 0) {
        onSelect(selected, remember);
      }
    } else {
      const selected = airlines.find((a) => a.airline_id === selectedId);
      if (selected) {
        onSelect(selected, remember);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const hasSelection = multiSelect ? selectedIds.size > 0 : !!selectedId;
    if (e.key === 'Enter' && hasSelection) {
      handleSubmit();
    }
  };

  // ===================================================================
  // Render Helpers
  // ===================================================================

  const getAirlineIcon = (code: string, isSelected: boolean = false) => {
    const iconClass = isSelected ? 'w-8 h-8 text-white' : 'w-8 h-8 text-sky-500';
    if (code === 'GLOBAL') {
      return <Globe size={32} className={iconClass} />;
    }
    return <Building size={32} className={isSelected ? 'w-8 h-8 text-white' : 'w-8 h-8 text-navy-600'} />;
  };

  const getRoleBadge = (role?: string, isSelected: boolean = false) => {
    if (!role) return null;

    const colors = isSelected
      ? {
          admin: 'bg-white/20 text-white border-white/30',
          editor: 'bg-white/20 text-white border-white/30',
          viewer: 'bg-white/20 text-white border-white/30',
        }
      : {
          admin: 'bg-red-100 text-red-800 border-red-200',
          editor: 'bg-blue-100 text-blue-800 border-blue-200',
          viewer: 'bg-gray-100 text-gray-800 border-gray-200',
        };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colors[role as keyof typeof colors]}`}
      >
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const getAirlineStats = (airlineId: string) => {
    return stats.find((s) => s.airline_id === airlineId);
  };

  // ===================================================================
  // Render
  // ===================================================================

  const hasSelection = multiSelect ? selectedIds.size > 0 : !!selectedId;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center lg:justify-end pt-[8vh] lg:pr-[8%] px-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <FocusLock returnFocus>
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Select Your Airline</h2>
            {canDismiss && (
              <button
                onClick={() => {}}
                className="text-gray-400 hover:text-gray-500"
                aria-label="Close"
              >
                <X size={24} className="w-6 h-6" />
              </button>
            )}
          </div>
          {userName && (
            <p className="mt-1 text-sm text-gray-600">Welcome back, {userName}!</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Please select {multiSelect ? 'one or more airlines' : 'which airline'} you'd like to access:
          </p>
          {multiSelect && selectedIds.size > 0 && (
            <p className="mt-1 text-xs font-medium text-sky-600">
              {selectedIds.size} airline{selectedIds.size > 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        {/* Airlines List - Scrollable with fixed height */}
        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-0">
          <div className="space-y-1.5">
            {airlines.map((airline) => {
              const isSelected = multiSelect
                ? selectedIds.has(airline.airline_id)
                : airline.airline_id === selectedId;
              const airlineStats = getAirlineStats(airline.airline_id);

              console.log(`[AirlineSelectionModal] Rendering ${airline.name || airline.code}: isSelected=${isSelected}`);

              return (
                <button
                  key={airline.airline_id}
                  onClick={() => handleToggleAirline(airline.airline_id)}
                  data-selected={isSelected}
                  data-airline={airline.name || airline.code}
                  style={isSelected ? { backgroundColor: '#0284c7', borderColor: '#0284c7', color: 'white' } : undefined}
                  className={
                    isSelected
                      ? 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border-2 shadow-md transition-all'
                      : 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border-2 border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 transition-all'
                  }
                >
                  {/* Checkbox (multi-select) or Radio Button (single-select) */}
                  <div className="flex-shrink-0">
                    {multiSelect ? (
                      // Checkbox
                      <div
                        style={isSelected ? { 
                          backgroundColor: 'white', 
                          borderColor: 'white',
                          borderWidth: '2px'
                        } : { 
                          backgroundColor: 'white', 
                          borderColor: '#9ca3af',
                          borderWidth: '2px'
                        }}
                        className="w-5 h-5 rounded flex items-center justify-center transition-colors"
                      >
                        {isSelected && (
                          <svg
                            className="w-4 h-4"
                            style={{ stroke: '#0284c7' }}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="3"
                            viewBox="0 0 24 24"
                          >
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    ) : (
                      // Radio Button
                      <div
                        style={isSelected ? { 
                          backgroundColor: 'white', 
                          borderColor: 'white',
                          borderWidth: '2px'
                        } : { 
                          backgroundColor: 'white', 
                          borderColor: '#9ca3af',
                          borderWidth: '2px'
                        }}
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                      >
                        {isSelected && (
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#0284c7' }} />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {getAirlineIcon(airline.code, isSelected)}
                  </div>

                  {/* Airline Info */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className={`text-base font-semibold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                        {airline.name || airline.code || 'Unknown Airline'}
                      </h3>
                      {airline.is_default && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          isSelected ? 'bg-white/20 text-white' : 'text-sky-600 bg-sky-100'
                        }`}>
                          Default
                        </span>
                      )}
                    </div>

                    <div className={`mt-0.5 flex items-center gap-3 text-sm ${isSelected ? 'text-white/90' : 'text-gray-600'}`}>
                      {airline.role && <span>Role: {getRoleBadge(airline.role, isSelected)}</span>}
                      {airlineStats && airlineStats.doc_count > 0 && (
                        <>
                          <span className={isSelected ? 'text-white/60' : 'text-gray-400'}>•</span>
                          <span>{airlineStats.doc_count} documents</span>
                        </>
                      )}
                    </div>

                    {airlineStats?.last_access && (
                      <div className={`mt-1 flex items-center gap-1 text-xs ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                        <Clock size={14} className="w-3.5 h-3.5" />
                        <span>Last visit: {airlineStats.last_access}</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer - Sticky at bottom */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 text-sky-600 border-gray-300 rounded focus:ring-sky-500"
              />
              <span>Remember this choice for 7 days</span>
            </label>

            <button
              onClick={handleSubmit}
              disabled={!hasSelection}
              style={!hasSelection ? undefined : { backgroundColor: '#0284c7', color: 'white' }}
              className="px-6 py-2.5 text-sm font-semibold rounded-lg hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
              onMouseEnter={(e) => {
                if (hasSelection) {
                  e.currentTarget.style.backgroundColor = '#0369a1'; // sky-700
                }
              }}
              onMouseLeave={(e) => {
                if (hasSelection) {
                  e.currentTarget.style.backgroundColor = '#0284c7'; // sky-600
                }
              }}
            >
              Continue {multiSelect && selectedIds.size > 0 && `(${selectedIds.size})`}
            </button>
          </div>
        </div>
      </div>
      </FocusLock>
    </div>
  );
};

// =====================================================================
// Simplified Version (for quick selection)
// =====================================================================

export const AirlineSelectionModalSimple: React.FC<{
  airlines: UserAllowedAirline[];
  onSelect: (airline: UserAllowedAirline) => void;
}> = ({ airlines, onSelect }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Select Airline
        </h2>

        <div className="space-y-2">
          {airlines.map((airline) => (
            <button
              key={airline.airline_id}
              onClick={() => onSelect(airline)}
              className="w-full flex items-center gap-3 p-3 text-left rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <span className="flex-1 font-medium text-gray-900">{airline.name}</span>
              <span className="text-sm text-gray-500">{airline.code}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
